import { useCallback, useEffect, useState } from "react"
import { apiClient } from "@/lib/api"

interface PlatformUser {
  id: string
  easy_id: string
  email: string
  name: string
  role: string
  is_active: boolean
  is_platform_admin: boolean
  property_count: number
  active_session_count: number
  created_at: string
  last_login_at: string
}

export function PlatformUsersPage() {
  const [users, setUsers] = useState<PlatformUser[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [activeFilter, setActiveFilter] = useState<"" | "active" | "inactive">("")
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState("")
  const [loadingMore, setLoadingMore] = useState(false)

  const fetchUsers = useCallback(async (cursor?: string) => {
    const isLoadMore = !!cursor
    if (isLoadMore) setLoadingMore(true)
    else setLoading(true)

    try {
      const res = await apiClient.getPlatformUsers({
        search: search || undefined,
        active: activeFilter || undefined,
        cursor: cursor || undefined,
      })
      if (res.ok && res.users) {
        if (isLoadMore) setUsers((prev) => [...prev, ...res.users!])
        else setUsers(res.users)
        setHasMore(res.has_more ?? false)
        setNextCursor(res.next_cursor ?? "")
      }
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [search, activeFilter])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  return (
    <div>
      <div className="page-header">
        <h1 className="page-header__title">All Users</h1>
        <p className="page-header__subtitle">System-wide user management</p>
      </div>

      <div style={{ marginBottom: "var(--sf-space-4)", display: "flex", gap: "var(--sf-space-3)" }}>
        <input
          type="text"
          placeholder="Search by email or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="audit-filters__select"
          style={{ flex: 1 }}
        />
        <select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as "" | "active" | "inactive")}
          className="audit-filters__select"
        >
          <option value="">All Users</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>
      </div>

      <div className="section-card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "var(--sf-space-8)", textAlign: "center" }}>
            <div className="sf-spinner" />
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__title">No users found</div>
            <div className="empty-state__desc">Try adjusting your search or filters.</div>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Properties</th>
                  <th>Sessions</th>
                  <th>Status</th>
                  <th>Last Login</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <span>{u.email}</span>
                      {u.is_platform_admin && (
                        <span className="status-pill status-pill--healthy" style={{ marginLeft: 8, fontSize: "var(--sf-text-xs)" }}>
                          <span className="status-pill__dot" />
                          Admin
                        </span>
                      )}
                    </td>
                    <td>{u.name || "\u2014"}</td>
                    <td>{u.property_count}</td>
                    <td>{u.active_session_count}</td>
                    <td>
                      <span className={`status-pill status-pill--${u.is_active ? "healthy" : "neutral"}`}>
                        <span className="status-pill__dot" />
                        {u.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      {u.last_login_at
                        ? new Date(u.last_login_at).toLocaleDateString()
                        : "Never"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasMore && (
              <div style={{ padding: "var(--sf-space-4)", textAlign: "center" }}>
                <button
                  className="data-table__action"
                  disabled={loadingMore}
                  onClick={() => fetchUsers(nextCursor)}
                >
                  {loadingMore ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
